import { Checkbox } from "@common/components/ui/checkbox"
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import { Label } from "@common/components/ui/label"
import { cn } from "@common/lib/utils"
import { useId } from "react"
import type { FieldPathByValue, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"

const GROUP_LABEL = "text-small-regular text-muted-foreground"

const OPTION_DESCRIPTION = "text-extra-small-regular text-muted-foreground"

type BareProps<T extends FieldValues> = {
  name: Path<T>
  id?: string
  className?: string
  optional?: boolean
}

export function BareCheckboxField<T extends FieldValues>({
  name,
  id,
  className,
  optional,
}: BareProps<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <Checkbox
          id={id}
          className={className}
          checked={!!field.value}
          onCheckedChange={field.onChange}
          onBlur={field.onBlur}
          disabled={field.disabled}
          ref={field.ref}
          aria-required={!optional}
        />
      )}
    />
  )
}

type Props<T extends FieldValues> = BareProps<T> & { label: string }

export function CheckboxField<T extends FieldValues>({
  name,
  label,
  className,
  optional,
}: Props<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem
          className={cn(
            "flex flex-row items-center gap-2 space-y-0",
            className,
          )}
        >
          <FormControl>
            <BareCheckboxField<T> name={name} optional={optional} />
          </FormControl>
          <FieldLabel label={label} optional={optional} />
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export type CheckboxOption<V extends string> = {
  value: V
  label: string
  description?: string
}

export function CheckboxGroupField<T extends FieldValues, V extends string>({
  name,
  label,
  options,
}: {
  name: FieldPathByValue<T, V[]>
  label: string
  options: ReadonlyArray<CheckboxOption<V>>
}): React.ReactElement {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selected: V[] = field.value
        const toggle = (value: V, checked: boolean) =>
          field.onChange(
            checked
              ? [...selected, value]
              : selected.filter((current) => current !== value),
          )

        return (
          <FormItem role="group" aria-label={label} className="space-y-2">
            <div className={GROUP_LABEL}>{label}</div>
            {options.map((option) => (
              <CheckboxOptionRow
                key={option.value}
                option={option}
                checked={selected.includes(option.value)}
                onCheckedChange={(checked) => toggle(option.value, checked)}
              />
            ))}
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

function CheckboxOptionRow<V extends string>({
  option,
  checked,
  onCheckedChange,
}: {
  option: CheckboxOption<V>
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}): React.ReactElement {
  const id = useId()

  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <Label htmlFor={id} className="mb-0 text-small-medium">
          {option.label}
        </Label>
        {option.description && (
          <p className={OPTION_DESCRIPTION}>{option.description}</p>
        )}
      </div>
    </div>
  )
}
